import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import Detail from "./detail";
import Logs from "./logs";
import Icon from "../../../../icons";
import { DiaryListViewRightPanelLoader } from "../../../../components/SkeletonLoader";
import { getResolutionName } from "../../../../containers/WorkkardDetail/utils";
import { changeActiveTab, changeSelectedDiary, updateDiary } from "../../../../containers/Diaries/actions";
import LoaderComponent from '../../../../components/Loader';
import * as CommonUtil from '../../../../common/util';
import { DiaryActions, StringConstants } from "../../../../containers/WorkkardDetail/constants";
import { showDialogueBox, hideDialogueBox } from '../../../../components/DialogueBox/actions';
import DiaryCreation from "../../../CreationForms/DiaryCreation";
import ConfirmationTextBox from "../../../../components/ConfirmationPrompt";
import { getActivityDetail, getWorkkardDetailMasterData } from "../../../../containers/WorkkardDetail/actions/action";

const DiaryStatusBadge = ({ status }) => {
    if (!status) return null;
    const normalized = status.toLowerCase();
    return (
        <span className={`status mr workkardStatus ${getResolutionName(normalized, true)}`}>
            {getResolutionName(normalized)}
        </span>
    );
};

const TabNav = ({ activeTab, onTabClick }) => (
    <div data-test="DetailAndLogs Tab" className="tabNav diaryNav">
        <div className="tabs">
            <ul>
                <li><button type="button" onClick={() => onTabClick(0)} className={`btn-cc btn-cc__transparent ${activeTab === 0 ? "noteIcon active" : "noteIcon"}`}>
                    <Icon name="note" className="icon" /> <span>Details</span>
                </button></li>
                <li><button type="button" onClick={() => onTabClick(1)} className={`btn-cc btn-cc__transparent ${activeTab === 1 ? "attachIcon active" : "attachIcon"}`}>
                    <Icon name="logs" className="icon" /> <span>Logs</span>
                </button></li>
            </ul>
        </div>
    </div>
);

const RightLayout = ({ showMobileView, setshowMobileView }) => {
    const dispatch = useDispatch();

    const { listingData, selectedDiaryDetail, isRightLoading, activeTab, selectedDiary } = useSelector(state => state.diaries.listing, shallowEqual);
    const isLoading = useSelector(state => state.isLoading);
    const [showMenuAction, setShowMenuAction] = useState(false);
    const menuRef = useRef(null);

    const primaryAction = useMemo(() => selectedDiaryDetail.menuActions
        ? CommonUtil.getMenuActions(DiaryActions, selectedDiaryDetail.menuActions)[0]?.value
        : null,
        [selectedDiaryDetail?.menuActions]);

    const menuActions = useMemo(() =>
        selectedDiaryDetail.menuActions
            ? CommonUtil.getMenuActions(DiaryActions, selectedDiaryDetail.menuActions).slice(1)
            : [],
        [selectedDiaryDetail.menuActions]);

    // Outside click handler
    const handleOutsideClick = useCallback((e) => {
        if (menuRef.current && !menuRef.current.contains(e.target)) {
            setShowMenuAction(false);
        }
    }, []);

    useEffect(() => {
        document.addEventListener('mousedown', handleOutsideClick);
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, [handleOutsideClick]);

    // Dynamic positioning handler
    const calculateDynamicPosition = useCallback(() => {
        const detailBlock = activeTab === 0
            ? document.querySelector(".detailsData")
            : document.querySelector('.logDiaryList');
        if (!detailBlock) return;

        const title = document.querySelector(".completeTitle")?.getBoundingClientRect();
        const navBlock = document.querySelector(".diaryNav");
        if (!title || !navBlock) return;

        if (title.height > 42) {
            const topPosition = navBlock.getBoundingClientRect().top - navBlock.clientHeight + 15;
            detailBlock.style.top = `${topPosition}px`;
        }
    }, [activeTab]);

    useEffect(() => {
        calculateDynamicPosition();
    }, [isRightLoading, selectedDiaryDetail, activeTab, calculateDynamicPosition]);

    // Module data fetch
    useEffect(() => {
        if (!Object.keys(selectedDiaryDetail).length) return;
        if (selectedDiaryDetail.Module === 'activity') {
            dispatch(getActivityDetail({ actId: selectedDiaryDetail.ModuleId }));
        } else if (selectedDiaryDetail.Module === 'workkard') {
            dispatch(getWorkkardDetailMasterData({ cId: selectedDiaryDetail.ModuleId, doNotUpdateActivityListing: true }));
        }
    }, [selectedDiaryDetail.Id]);

    // Tab handler
    const onTabClick = useCallback((tabNumber) => {
        dispatch(changeActiveTab({ activeTab: tabNumber }));
    }, [dispatch]);

    const renderTabContent = () => activeTab === 0 ? <Detail /> : <Logs />;

    // Diary action helpers
    const openDiaryBox = useCallback((component) => {
        dispatch(showDialogueBox({
            flag: true,
            component,
            overlayClassName: "modal fullViewMain",
            contentClassName: "modalContent fullView animated faster fadeInUpx2 medium",
            shouldCloseOnOverlayClick: false,
        }));
    }, [dispatch]);

    const handleEditDiary = useCallback(() => {
        setShowMenuAction(false);
        openDiaryBox(
            <DiaryCreation
                type={StringConstants.common}
                onClose={() => dispatch(hideDialogueBox())}
                referrer="tdo"
                isEdit
                todoId={selectedDiary}
            />
        );
    }, [dispatch, openDiaryBox, selectedDiary]);

    const handleCompleteDiary = useCallback(() => {
        setShowMenuAction(false);
        dispatch(updateDiary({
            id: selectedDiary,
            status: "COMPLETE",
            referrer: "COMPLETED"
        }));
    }, [dispatch, selectedDiary]);

    const handleCancelDiary = useCallback(() => {
        setShowMenuAction(false);
        dispatch(showDialogueBox({
            flag: true,
            component: (
                <ConfirmationTextBox
                    className="modalBody"
                    message="Are you sure you wish to cancel this Diary?"
                    onCancelClick={() => dispatch(hideDialogueBox())}
                    onConfirmClick={() => {
                        dispatch(updateDiary({
                            id: selectedDiary,
                            status: "CANCEL",
                            referrer: "CANCELLED"
                        }));
                        dispatch(hideDialogueBox());
                    }}
                    confirmationText="Confirm"
                    footerClass="modalFooter"
                    cancelText="Cancel"
                />
            ),
            overlayClassName: "modal",
            contentClassName: "modalContent small animated faster fadeInUpx2",
            shouldCloseOnOverlayClick: true,
        }));
    }, [dispatch, selectedDiary]);

    const handleReopenDiary = useCallback(() => {
        setShowMenuAction(false);
        dispatch(updateDiary({
            id: selectedDiary,
            status: "OPEN",
            referrer: "REOPEN"
        }));
    }, [dispatch, selectedDiary]);

    // Action dispatcher
    const ACTION_MAP = useMemo(() => ({
        [StringConstants.EditDiary]: handleEditDiary,
        [StringConstants.CompleteDiary]: handleCompleteDiary,
        [StringConstants.CancelDiary]: handleCancelDiary,
        [StringConstants.ReopenDiary]: handleReopenDiary,
    }), [handleEditDiary, handleCompleteDiary, handleCancelDiary, handleReopenDiary]);

    const onClickDiaryActions = useCallback((e) => {
        const label = e.target.innerText.trim();
        ACTION_MAP[label]?.();
    }, [ACTION_MAP]);

    const onCloseDiaryPage = useCallback(() => {
        setshowMobileView(false);
        dispatch(changeSelectedDiary({ id: 0 }));
    }, [dispatch, setshowMobileView]);

    // Render guards
    const hasData = Object.keys(listingData).length > 0 && listingData.data?.length > 0;
    const layoutClass = `secondLayout diaresStatus${showMobileView ? ' diaresStatusMob' : ''}`;

    if (isLoading) {
        return <div className={layoutClass}><DiaryListViewRightPanelLoader /></div>
    }

    if (!hasData) {
        return (
            <div className={layoutClass}>
                <div data-test="No Data" className="noDataFound noFoundDiary label">
                    <h2>No Data Found</h2>
                </div>
            </div>
        );
    }

    if (!selectedDiary) {
        return (
            <div className={layoutClass}>
                <div data-test="No Diary" className="noDataFound noFoundDiary label">
                    <h2>No Diary Selected</h2>
                </div>
            </div>
        );
    }

    return (
        <div className={layoutClass}>
            {isRightLoading && <LoaderComponent />}

            <div className="filterBlock rowFlex">
                <div data-test="Title" className="filterLeft">
                    <h2 className="mr__1 completeTitle">{selectedDiaryDetail.title}</h2>
                    <div className="flexWrap selfStart mt">
                        <DiaryStatusBadge status={selectedDiaryDetail.status} />
                    </div>
                </div>

                {selectedDiaryDetail.status !== "CANCELLED" && (
                    <div data-test="Menu Action" className="filterRight selfStart">
                        <div className={showMenuAction ? "dropDown active" : "dropDown alignMiddle"} ref={menuRef}>
                            <a className="btn pointer buttonGradient newViewsBtn completeBtn">
                                <span onClick={onClickDiaryActions}>{primaryAction}</span>
                                <span
                                    className={`actionBtn alignMiddle ${showMenuAction ? 'active' : ''}`}
                                    onClick={() => setShowMenuAction(prev => !prev)}
                                >
                                    <Icon name="chev" className="icon" />
                                </span>
                            </a>

                            {showMobileView && (
                                <span className="iconRipple ml" onClick={onCloseDiaryPage}>
                                    <Icon name="close" className="icon close" />
                                </span>
                            )}

                            <div className={showMenuAction ? "dropDownMenu openRight rightAlign onClick" : "dropDownMenu openRight"}>
                                {menuActions.map((item, key) => (
                                    <span key={key} onClick={onClickDiaryActions}>
                                        <a href="javascript:void(0)">{item.value}</a>
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <TabNav activeTab={activeTab} onTabClick={onTabClick} />
            {renderTabContent()}
        </div>
    );
};

export default RightLayout;